// Import VM trading service for primary operations
import { vmTradingService } from './vmTradingService';
import { getApiConfig } from '@/config/api';

// Define API base URL directly (legacy fallback)
const API_BASE_URL = process.env.VITE_BACKEND_API_URL || 'https://stewie.api.clewis.me';

// Use proxy during development to avoid CORS issues
const isDevelopment = process.env.NODE_ENV === 'development';

// Use the actual available API endpoints based on Swagger documentation
const API_URL = isDevelopment 
  ? '/api'  // Use the main API proxy
  : API_BASE_URL;

export interface DeploymentSymbol {
  ticker: string;
  symbol?: string;
  name?: string;
  direction: string;
  weight: number;
}

export interface DeploymentSettings {
  mode: string;
  provider?: string;
  environment?: string;
  marketEnvironment?: string;
  initialCapital: string;
  includeCommission: boolean;
  commissionRate: string;
  includeSlippage: boolean;
  slippageModel: string;
  slippageValue: string;
  executionDelay: string;
  useRealTimeData: boolean;
  dataUpdateInterval: string;
  timeframe?: string;
}

export interface DeploymentModelSettings {
  type: string;
  weight: number;
  decisionControl: string;
  stewieQuantSettings?: any;
  lstmSettings?: any;
  arimaSettings?: any;
  mlSettings?: any;
  xgboostSettings?: any;
  rlSettings?: any;
  hdkSettings?: any;
}

export interface DeploymentBasketSymbol {
  ticker: string;
  symbol?: string;
  direction: string;
  weight: number;
  profitTarget?: string;
  stopLoss?: string;
  valueAtRisk?: string;
}

export interface DeploymentBasket {
  id: string;
  name: string;
  symbols: DeploymentBasketSymbol[];
  profitTarget?: string;
  stopLoss?: string;
  valueAtRisk?: string;
  isAiManaged: boolean;
}

export interface DeploymentStrategy {
  id?: string;
  name?: string;
  engine?: string;
  models: DeploymentModelSettings[];
  entryConditions?: any;
  exitConditions?: any;
  riskManagement?: any;
  baskets?: DeploymentBasket[];
}

export interface DeploymentConfig {
  isActive: boolean;
  isPaperTrading: boolean;
  accountId: string;
  ibkrSettings?: any;
  notificationsEnabled: boolean;
}

export interface DeploymentRequest {
  strategy_id: string;
  symbols: DeploymentSymbol[] | string[];
  settings: DeploymentSettings;
  strategy?: DeploymentStrategy;
  baskets?: DeploymentBasket[];
  deploymentConfig?: DeploymentConfig;
}

export interface DeploymentStatus {
  strategy_id: string;
  status: string;
  deployment_time: string;
  last_signal_time?: string;
  active_positions: number;
  total_pnl: number;
  message?: string;
  strategy_config?: any; // Full strategy configuration including execution timing
}

export interface DeploymentResponse {
  success: boolean;
  strategy_id: string;
  deployment_status: DeploymentStatus;
  message: string;
  details?: any;
}

class DeploymentService {
  private baseUrl: string;

  constructor() {
    // Use HTTPS domain to fix Mixed Content Policy with production frontend
    const config = getApiConfig();
    this.baseUrl = config.TRADING_BASE_URL;
  }

  /**
   * Get authentication headers for backend requests
   */
  private getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    return headers;
  }

  /**
   * Deploy strategy using available buy/sell endpoints
   * Since /api/deployment/deploy doesn't exist, we'll simulate deployment
   * by validating the strategy and indicating it's ready for manual execution
   */
  async deployStrategy(deploymentRequest: any): Promise<any> {
    console.log('DeploymentService: 🚀 REAL STRATEGY DEPLOYMENT STARTING:', deploymentRequest);
    
    try {
      // Extract and validate required data
      const strategy = deploymentRequest.strategy || {};
      const symbols = deploymentRequest.symbols || strategy.symbols || [];
      const settings = deploymentRequest.settings || {};
      
      if (!deploymentRequest.strategy_id && !strategy.id) {
        throw new Error('Missing strategy_id for deployment');
      }
      
      if (!symbols || symbols.length === 0) {
        throw new Error('Missing symbols for deployment');
      }

      // Map frontend data structure to VM expected format
      const vmPayload = this.mapFrontendToVMFormat(deploymentRequest);
      
      console.log('DeploymentService: 📤 Sending payload to VM:', JSON.stringify(vmPayload, null, 2));
      
      // REAL DEPLOYMENT: Call VM's /api/strategies/start endpoint
      const response = await fetch(`${this.baseUrl}/api/strategies/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...this.getAuthHeaders(),
        },
        body: JSON.stringify(vmPayload),
      });

      const responseText = await response.text();
      let responseData;
      
      try {
        responseData = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error(`VM returned invalid JSON: ${responseText}`);
      }

      if (!response.ok) {
        console.error('DeploymentService: ❌ VM deployment failed:', response.status, responseData);
        throw new Error(`VM deployment failed (${response.status}): ${responseData.error || responseData.message || 'Unknown error'}`);
      }

      if (!responseData.success) {
        console.error('DeploymentService: ❌ VM rejected deployment:', responseData);
        throw new Error(`VM rejected deployment: ${responseData.error || 'Unknown error'}`);
      }

      console.log('DeploymentService: ✅ VM DEPLOYMENT SUCCESSFUL:', responseData);
      
      // Map VM response back to frontend format
      return {
        success: true,
        deployment_id: responseData.data.strategy_id,
        strategy_id: responseData.data.strategy_id,
        status: 'deployed',
        message: responseData.data.message || 'Strategy deployed successfully to VM',
        vm_response: responseData.data,
        deployment_time: responseData.data.started_at,
        trading_mode: responseData.data.trading_mode,
        symbols: responseData.data.symbols,
        execution_timing: responseData.data.execution_timing,
        background_trading_active: responseData.data.background_trading_active,
        optimal_loop_frequency: responseData.data.optimal_loop_frequency,
        timing_info: responseData.data.timing_info
      };
      
    } catch (error) {
      console.error('DeploymentService: 💥 DEPLOYMENT FAILED:', error);
      throw new Error(`Deployment failed: ${error.message}`);
    }
  }

  /**
   * Map frontend camelCase data structure to VM snake_case format
   */
  private mapFrontendToVMFormat(deploymentRequest: any): any {
    const strategy = deploymentRequest.strategy || {};
    const symbols = deploymentRequest.symbols || strategy.symbols || [];
    const settings = deploymentRequest.settings || {};
    
    // Debug logging for development
    if (process.env.NODE_ENV === 'development') {
      console.log('DeploymentService: Mapping frontend data to VM format:', {
        deploymentRequest: deploymentRequest.strategy_id,
        strategyName: strategy.name,
        executionTiming: deploymentRequest.execution_timing,
        riskManagement: !!strategy.riskManagement
      });
    }
    
    // Extract symbols in VM format with individual risk parameters
    const vmSymbols = Array.isArray(symbols) 
      ? symbols.map(s => typeof s === 'string' ? s : (s.ticker || s.symbol))
      : typeof symbols === 'string' 
      ? symbols.split(',').map(s => s.trim())
      : ['SPY']; // Default fallback

    // 🎯 NEW: Extract individual symbol risk parameters
    const symbolRiskParams = Array.isArray(symbols) 
      ? symbols
          .filter(s => typeof s !== 'string' && (s.profitTarget || s.stopLoss))
          .map(s => ({
            symbol: s.ticker || s.symbol,
            profit_target: s.profitTarget ? parseFloat(s.profitTarget) : undefined,
            stop_loss: s.stopLoss ? parseFloat(s.stopLoss) : undefined
          }))
          .filter(s => s.profit_target !== undefined || s.stop_loss !== undefined)
      : [];

    // Debug logging for individual symbol parameters
    if (process.env.NODE_ENV === 'development') {
      console.log('DeploymentService: 🎯 SYMBOL-SPECIFIC RISK PARAMETERS:', symbolRiskParams);
      if (symbolRiskParams.length > 0) {
        symbolRiskParams.forEach(param => {
          console.log(`  - ${param.symbol}: ${param.profit_target}% profit target, ${param.stop_loss}% stop loss`);
        });
      } else {
        console.log('  - No individual symbol parameters found, using strategy defaults');
      }
    }

    // Map execution timing - prioritize top-level deploymentRequest.execution_timing
    const executionTiming = deploymentRequest.execution_timing || strategy.executionTiming || strategy.execution_timing || {};
    const vmExecutionTiming = {
      signalFrequency: executionTiming.signalFrequency || executionTiming.signal_frequency || 'daily_close',
      monitorFrequency: executionTiming.monitorFrequency || executionTiming.monitor_frequency || '5min',
      executeRthOnly: executionTiming.executeRthOnly !== undefined 
        ? executionTiming.executeRthOnly 
        : executionTiming.execute_rth_only !== undefined
        ? executionTiming.execute_rth_only
        : true,
      allowPrePostMarket: executionTiming.allowPrePostMarket !== undefined
        ? executionTiming.allowPrePostMarket
        : executionTiming.allow_pre_post_market !== undefined
        ? executionTiming.allow_pre_post_market
        : false
    };

    // Debug logging for development
    if (process.env.NODE_ENV === 'development') {
      console.log('DeploymentService: Mapped execution timing:', vmExecutionTiming);
    }

    // Map risk parameters - check strategy.riskManagement first (newer format)
    const riskParams = strategy.riskManagement || strategy.riskParameters || strategy.risk_params || {};
    
    // Helper function to safely parse numeric risk values
    const parseRiskValue = (value: any, defaultValue: number): number => {
      if (value === undefined || value === null || value === '') return defaultValue;
      const parsed = typeof value === 'string' ? parseFloat(value) : Number(value);
      return isNaN(parsed) ? defaultValue : parsed;
    };

    // Global risk parameters (used as defaults for symbols without individual parameters)
    const vmRiskParams = {
      max_position_value: riskParams.maxPositionValue || 
                         riskParams.max_position_value || 
                         (strategy.allocatedCapital && parseFloat(strategy.allocatedCapital) * 0.1) || 
                         1000,
      max_position_size: (() => {
        // Handle maxPositionSizePct - check if it's already a decimal (0.65) or percentage (65)
        if (riskParams.maxPositionSizePct !== undefined) {
          const value = parseFloat(riskParams.maxPositionSizePct);
          // If value is > 1, assume it's a percentage and convert to decimal
          return value > 1 ? value / 100 : value;
        }
        if (riskParams.maxPositionSize !== undefined) {
          return parseFloat(riskParams.maxPositionSize);
        }
        if (riskParams.max_position_size !== undefined) {
          return parseFloat(riskParams.max_position_size);
        }
        return 0.05; // 5% default
      })(),
      stop_loss: parseRiskValue(
        riskParams.stopLossValue || riskParams.stopLoss || riskParams.stop_loss,
        2.0
      ),
      profit_target: parseRiskValue(
        riskParams.profitTargetValue || riskParams.profitTarget || riskParams.takeProfit || 
        riskParams.profit_target || riskParams.take_profit,
        5.0
      )
    };

    // Debug logging for development
    if (process.env.NODE_ENV === 'development') {
      console.log('DeploymentService: Mapped global risk parameters:', vmRiskParams);
    }

    // Determine trading mode - VM expects UPPERCASE
    const tradingMode = settings.mode === 'live' ? 'LIVE' : 'PAPER';

    // Create VM-compatible payload with individual symbol parameters
    const vmPayload: any = {
      strategy_id: deploymentRequest.strategy_id || strategy.id || `strategy_${Date.now()}`,
      name: strategy.name || 'Deployed Strategy',
      symbols: vmSymbols,
      trading_mode: tradingMode,
      risk_params: vmRiskParams, // Global defaults
      execution_timing: vmExecutionTiming,
      // Include AI engine and models information
      engine: strategy.engine || 'default',
      models: strategy.models || [],
      // Include allocated capital
      allocated_capital: strategy.allocatedCapital || strategy.allocated_capital,
      created_from: 'frontend_deployment'
    };

    // 🎯 Add individual symbol risk parameters if they exist
    if (symbolRiskParams.length > 0) {
      vmPayload.symbol_risk_params = symbolRiskParams;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('DeploymentService: ✅ Added symbol-specific risk parameters to VM payload');
      }
    }

    return vmPayload;
  }

  /**
   * Get deployment status and strategy configuration from VM
   */
  async getDeploymentStatus(strategyId: string): Promise<any> {
    console.log('DeploymentService: Getting deployment status for:', strategyId);
    
    try {
      // UPDATED: Use new VM deployed strategies endpoint
      const response = await fetch(`${this.baseUrl}/api/strategies/deployed`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (response.ok) {
        const strategiesData = await response.json();
        console.log('DeploymentService: Raw VM deployed strategies response:', strategiesData);
        
        // Handle deployed strategies response structure
        let strategies = [];
        if (strategiesData.success && strategiesData.data?.strategies) {
          // New deployed strategies endpoint returns array directly
          strategies = strategiesData.data.strategies;
          console.log('DeploymentService: Found deployed strategies array:', strategies);
          console.log(`DeploymentService: VM reports ${strategiesData.data.count} deployed strategies with status: ${strategiesData.data.status}`);
        } else if (strategiesData.success && strategiesData.data?.active_strategies) {
          // Legacy fallback: if old format is used
          strategies = Object.values(strategiesData.data.active_strategies);
          console.log('DeploymentService: Found legacy strategies object:', strategies);
        }
        
        // Find the strategy by ID - enhanced matching for ID mismatch issues
        let strategy = strategies.find(s => s.id === strategyId || s.strategy_id === strategyId);
        
        // If not found by direct ID match, try enhanced fallback matching including name matching
        // This handles cases where frontend UUID != deployment ID
        if (!strategy && strategies.length > 0) {
          if (process.env.NODE_ENV === 'development') {
            console.log('DeploymentService: Direct ID match failed, attempting enhanced fallback matching');
            console.log('DeploymentService: Looking for strategyId:', strategyId);
            console.log('DeploymentService: Available VM strategies:', strategies.map(s => ({ 
              id: s.id, 
              strategy_id: s.strategy_id, 
              name: s.name,
              created_at: s.created_at || s.started_at
            })));
          }
          
          // Enhanced matching: look for most recent deployment with similar characteristics
          strategy = strategies
            .filter(s => {
              const timeDiff = new Date().getTime() - new Date(s.created_at || s.started_at || 0).getTime();
              const isRecent = timeDiff < 15 * 60 * 1000; // Within last 15 minutes
              
              // Try name-based matching for better accuracy  
              const nameWords = (s.name || '').toLowerCase().split(/\s+/);
              const hasCommonWords = nameWords.some(word => 
                word.includes('july') || word.includes('semis') || word.includes('2025') ||
                word.includes('evan') || word.includes('etf') || word.includes('31')
              );
              
              // Also try partial ID matching (first part of UUID)
              const strategyIdShort = strategyId.split('-')[0];
              const vmIdMatches = (s.id || '').includes(strategyIdShort) || (s.strategy_id || '').includes(strategyIdShort);
              
              return isRecent || hasCommonWords || vmIdMatches;
            })
            .sort((a, b) => {
              // Sort by most recent first
              const timeA = new Date(a.created_at || a.started_at || 0).getTime();
              const timeB = new Date(b.created_at || b.started_at || 0).getTime();
              return timeB - timeA;
            })[0]; // Take the most recent match
          
          // Final fallback: if still no match and there's only one strategy, use it
          if (!strategy && strategies.length === 1) {
            strategy = strategies[0];
            if (process.env.NODE_ENV === 'development') {
              console.log('DeploymentService: Using single available strategy as fallback:', strategy.name);
            }
          }
          
          if (strategy && process.env.NODE_ENV === 'development') {
            console.log('DeploymentService: Found strategy via enhanced fallback matching:', {
              vm_id: strategy.id || strategy.strategy_id,
              vm_name: strategy.name,
              created_at: strategy.created_at || strategy.started_at
            });
          }
        }
        
        if (strategy) {
          console.log('DeploymentService: Found strategy data:', strategy);
          console.log('DeploymentService: Strategy symbols:', strategy.symbols);
          console.log('DeploymentService: Strategy execution_timing:', strategy.execution_timing);
          console.log('DeploymentService: Strategy risk_params:', strategy.risk_params);
          console.log('DeploymentService: Strategy symbol_risk_params:', strategy.symbol_risk_params);
          
          // Convert VM format to frontend format with proper field mapping
          return {
            strategy_id: strategyId,
            status: strategy.status || 'running',
            deployment_time: strategy.created_at || strategy.started_at || strategy.added_at || new Date().toISOString(),
            active_positions: strategy.positions || strategy.active_positions || 0,
            total_pnl: strategy.pnl || strategy.total_pnl || 0.0,
            last_signal_time: strategy.last_update || strategy.last_signal_time || strategy.last_processed || strategy.last_signal_execution,
            message: `Strategy running with ${strategy.execution_timing?.signal_frequency || strategy.execution_timing?.signalFrequency || 'default'} frequency`,
            
            // Include full strategy configuration for strategy details dialog
            strategy_config: {
              ...strategy,
              // Map execution_timing (snake_case) to executionTiming (camelCase) for frontend
              executionTiming: strategy.execution_timing || strategy.executionTiming,
              // Preserve original field for compatibility
              execution_timing: strategy.execution_timing,
              // Map risk_params (snake_case) to riskParameters (camelCase) for frontend
              riskParameters: strategy.risk_params || strategy.riskParameters,
              // Preserve original field for compatibility
              risk_params: strategy.risk_params,
              // Include symbol-specific risk parameters
              symbolRiskParams: strategy.symbol_risk_params || strategy.symbolRiskParams,
              // Preserve original field for compatibility
              symbol_risk_params: strategy.symbol_risk_params,
              // Include allocated capital
              allocatedCapital: strategy.allocated_capital || strategy.allocatedCapital,
              // Preserve original field for compatibility
              allocated_capital: strategy.allocated_capital,
              // Ensure symbols are properly mapped - this is the key fix!
              symbols: strategy.symbols || [],
              // Add engine info if available
              engine: strategy.engine || 'static',
              // Add models info if available
              models: strategy.models || []
            }
          };
        } else {
          console.log('DeploymentService: Strategy not found in VM response for ID:', strategyId);
        }
      } else {
        console.log('DeploymentService: VM strategies fetch failed:', response.status, response.statusText);
      }
    } catch (error) {
      console.warn('DeploymentService: Failed to fetch strategy from VM:', error);
    }
    
    // Fallback to basic status if VM fetch fails
    console.log('DeploymentService: Using fallback status for strategy:', strategyId);
    return {
      strategy_id: strategyId,
      status: 'manual_ready',
      message: 'Strategy ready for manual execution via buy/sell endpoints',
      deployment_time: new Date().toISOString(),
      active_positions: 0,
      total_pnl: 0.0,
      last_signal_time: null,
      endpoints_available: ['buy', 'sell', 'account', 'orders', 'positions'],
      strategy_config: {
        symbols: [],
        execution_timing: null,
        risk_params: null
      }
    };
  }

  /**
   * Test authentication with multiple methods
   */
  async testAuthentication(): Promise<Array<{method: string, success: boolean, details?: any}>> {
    const results = [];
    
    // Test 1: Basic account endpoint
    try {
      const response = await fetch(`${this.baseUrl}/account`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      
      results.push({
        method: 'GET /account',
        success: response.ok,
        details: {
          status: response.status,
          statusText: response.statusText,
          url: response.url
        }
      });
    } catch (error) {
      results.push({
        method: 'GET /account',
        success: false,
        details: { error: error.message }
      });
    }

    // Test 2: Market clock endpoint
    try {
      const response = await fetch(`${this.baseUrl}/clock`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      
      results.push({
        method: 'GET /clock',
        success: response.ok,
        details: {
          status: response.status,
          statusText: response.statusText,
          url: response.url
        }
      });
    } catch (error) {
      results.push({
        method: 'GET /clock',
        success: false,
        details: { error: error.message }
      });
    }

    return results;
  }

  /**
   * Test direct backend access using actual available endpoints
   */
  async testDirectBackend(): Promise<{success: boolean, details?: any}> {
    try {
      console.log(`Testing direct backend access to: ${this.baseUrl}/account`);
      
      const response = await fetch(`${this.baseUrl}/account`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const responseText = await response.text();
      let responseData;
      
      try {
        responseData = JSON.parse(responseText);
      } catch {
        responseData = responseText;
      }

      console.log('Direct backend test response:', {
        status: response.status,
        statusText: response.statusText,
        data: responseData
      });

      return {
        success: response.ok,
        details: {
          status: response.status,
          statusText: response.statusText,
          data: responseData,
          url: response.url
        }
      };
    } catch (error) {
      console.error('Direct backend test failed:', error);
      return {
        success: false,
        details: { error: error.message }
      };
    }
  }

  /**
   * Simple deployment test using actual API structure
   */
  async deployStrategySimple(strategyJSON: any): Promise<{success: boolean, details?: any}> {
    try {
      console.log('Testing simple deployment with strategy:', strategyJSON.strategy_id);
      
      // Since deployment endpoint doesn't exist, we'll test connectivity
      // and return a success indicating the strategy is ready for manual execution
      const connectionTest = await this.testDirectBackend();
      
      if (connectionTest.success) {
        return {
          success: true,
          details: {
            message: 'Strategy validated successfully',
            strategy_id: strategyJSON.strategy_id,
            ready_for_manual_execution: true,
            available_endpoints: {
              buy: `${this.baseUrl}/buy`,
              sell: `${this.baseUrl}/sell`,
              account: `${this.baseUrl}/account`,
              orders: `${this.baseUrl}/orders`,
              positions: `${this.baseUrl}/positions`
            }
          }
        };
      } else {
        return {
          success: false,
          details: {
            message: 'Cannot connect to trading API',
            connection_test: connectionTest
          }
        };
      }
    } catch (error) {
      console.error('Simple deployment test failed:', error);
      return {
        success: false,
        details: { error: error.message }
      };
    }
  }

  /**
   * Place buy order - prioritizes VM trading service with DYNAMIC trading mode
   */
  async placeBuyOrder(orderData: any): Promise<any> {
    try {
      console.log('DeploymentService: Placing buy order via VM with trading mode:', orderData);
      
      // Extract trading mode from order metadata
      const tradingMode = orderData.metadata?.trading_mode || 'paper';
      console.log(`DeploymentService: Trading mode for buy order: ${tradingMode.toUpperCase()}`);
      
      // Convert order data to VM format
      const vmOrderData = {
        symbol: orderData.symbol,
        qty: orderData.qty || orderData.quantity,
        side: 'buy',
        type: orderData.type || 'market',
        time_in_force: orderData.time_in_force || 'day',
        ...orderData
      };
      
      const result = await vmTradingService.placeOrder(vmOrderData);
      console.log('DeploymentService: VM buy order successful:', result);
      return result;
      
    } catch (vmError) {
      console.warn('DeploymentService: VM buy order failed, trying direct VM API:', vmError);
      
      // Fallback to direct VM API call with trading mode
      try {
        // Extract trading mode from order metadata  
        const tradingMode = orderData.metadata?.trading_mode || 'paper';
        console.log(`DeploymentService: Using direct VM API with trading mode: ${tradingMode.toUpperCase()}`);
        
        // CRITICAL: Send request to VM with proper trading mode
        const response = await fetch(`${this.baseUrl}/api/orders`, {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify({
            ...orderData,
            // Ensure trading mode is passed to VM
            metadata: {
              ...orderData.metadata,
              trading_mode: tradingMode
            }
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`VM order failed: ${response.status} ${response.statusText} - ${errorText}`);
        }

        const result = await response.json();
        console.log('DeploymentService: Direct VM buy order successful:', result);
        return result;
      } catch (directError) {
        console.error('DeploymentService: Both VM service and direct VM API failed:', directError);
        throw new Error(`All buy order methods failed. VM service: ${vmError.message}, Direct VM: ${directError.message}`);
      }
    }
  }

  /**
   * Place sell order - prioritizes VM trading service with DYNAMIC trading mode
   */
  async placeSellOrder(orderData: any): Promise<any> {
    try {
      console.log('DeploymentService: Placing sell order via VM with trading mode:', orderData);
      
      // Extract trading mode from order metadata
      const tradingMode = orderData.metadata?.trading_mode || 'paper';
      console.log(`DeploymentService: Trading mode for sell order: ${tradingMode.toUpperCase()}`);
      
      // Convert order data to VM format
      const vmOrderData = {
        symbol: orderData.symbol,
        qty: orderData.qty || orderData.quantity,
        side: 'sell',
        type: orderData.type || 'market',
        time_in_force: orderData.time_in_force || 'day',
        ...orderData
      };
      
      const result = await vmTradingService.placeOrder(vmOrderData);
      console.log('DeploymentService: VM sell order successful:', result);
      return result;
      
    } catch (vmError) {
      console.warn('DeploymentService: VM sell order failed, trying direct VM API:', vmError);
      
      // Fallback to direct VM API call with trading mode
      try {
        // Extract trading mode from order metadata  
        const tradingMode = orderData.metadata?.trading_mode || 'paper';
        console.log(`DeploymentService: Using direct VM API with trading mode: ${tradingMode.toUpperCase()}`);
        
        // CRITICAL: Send request to VM with proper trading mode
        const response = await fetch(`${this.baseUrl}/api/orders`, {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify({
            ...orderData,
            // Ensure trading mode is passed to VM
            metadata: {
              ...orderData.metadata,
              trading_mode: tradingMode
            }
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`VM order failed: ${response.status} ${response.statusText} - ${errorText}`);
        }

        const result = await response.json();
        console.log('DeploymentService: Direct VM sell order successful:', result);
        return result;
      } catch (directError) {
        console.error('DeploymentService: Both VM service and direct VM API failed:', directError);
        throw new Error(`All sell order methods failed. VM service: ${vmError.message}, Direct VM: ${directError.message}`);
      }
    }
  }

  /**
   * Get account information - prioritizes VM trading service
   */
  async getAccountInfo(tradingMode: string = 'paper'): Promise<any> {
    try {
      // Delegate to vmTradingService for consistent API handling and error fallback
      console.log(`DeploymentService: Getting ${tradingMode.toUpperCase()} account info via vmTradingService`);
      const accountInfo = await vmTradingService.getAccount(tradingMode);
      
      if (!accountInfo) {
        throw new Error('No account information received from VM trading service.');
      }

      return accountInfo;
    } catch (error) {
      console.error(`Account info error for ${tradingMode} mode:`, error);
      throw error;
    }
  }

  /**
   * Get orders using actual /orders endpoint
   */
  async getOrders(tradingMode: string = 'paper'): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/api/orders?trading_mode=${tradingMode}`);
      
      if (!response.ok) {
        throw new Error(`Orders fetch failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Orders fetch error:', error);
      throw error;
    }
  }

  /**
   * Get positions using actual /positions endpoint
   */
  async getPositions(tradingMode: string = 'paper'): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/api/positions?trading_mode=${tradingMode}`);
      
      if (!response.ok) {
        throw new Error(`Positions fetch failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Positions fetch error:', error);
      throw error;
    }
  }

  /**
   * Check VM status and available endpoints
   */
  async getVMStatus(): Promise<{isSimulationMode: boolean, availableEndpoints: string[], status: string}> {
    try {
      const vmAccount = await vmTradingService.getAccount();
      const vmOrders = await vmTradingService.getOrders();
      
      const isSimulationMode = vmOrders && vmOrders._metadata && vmOrders._metadata.vm_status === 'orders_endpoint_unavailable';
      
      return {
        isSimulationMode,
        availableEndpoints: ['portfolio', 'account', isSimulationMode ? 'orders(simulation)' : 'orders'],
        status: isSimulationMode ? 'VM running in simulation mode for orders' : 'VM fully operational'
      };
    } catch (error) {
      return {
        isSimulationMode: true,
        availableEndpoints: ['error'],
        status: `VM connection failed: ${error.message}`
      };
    }
  }

  /**
   * List deployed strategies - UPDATED for new VM endpoint
   */
  async listDeployedStrategies(): Promise<any> {
    console.log('DeploymentService: 📋 Fetching deployed strategies from VM...');
    
    try {
      // UPDATED: Use new VM deployed strategies endpoint
      const response = await fetch(`${this.baseUrl}/api/strategies/deployed`);
      
      if (!response.ok) {
        throw new Error(`Deployed strategies fetch failed: ${response.status}`);
      }

      const result = await response.json();
      console.log('DeploymentService: ✅ Deployed strategies response:', result);
      
      if (result.success && result.data) {
        console.log(`DeploymentService: Found ${result.data.count} deployed strategies`);
        
        // Map to expected format for frontend compatibility
        return {
          success: true,
          deployed_strategies: result.data.strategies.map(strategy => ({
            strategy_id: strategy.strategy_id,
            status: strategy.status || 'active',
            deployment_time: strategy.started_at || strategy.created_at,
            is_paper_trading: strategy.trading_mode === 'PAPER',
            symbols_count: Array.isArray(strategy.symbols) ? strategy.symbols.length : 0,
            baskets_count: 0, // Not currently used in VM
            name: strategy.name,
            trading_mode: strategy.trading_mode,
            symbols: strategy.symbols || []
          })),
          total_count: result.data.count
        };
      }
      
      return result;
    } catch (error) {
      console.error('DeploymentService: Strategy list error:', error);
      throw error;
    }
  }

  /**
   * Stop a deployed strategy - REAL VM INTEGRATION
   */
  async stopStrategy(deploymentId: string): Promise<any> {
    console.log('DeploymentService: 🛑 STOPPING STRATEGY ON VM:', deploymentId);
    
    try {
      // REAL STOP: Call VM's /api/strategies/stop/{id} endpoint
      const response = await fetch(`${this.baseUrl}/api/strategies/stop/${deploymentId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...this.getAuthHeaders(),
        },
      });

      const responseText = await response.text();
      let responseData;
      
      try {
        responseData = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error(`VM returned invalid JSON: ${responseText}`);
      }

      if (!response.ok) {
        if (response.status === 404) {
          // Check if this is an endpoint 404 (stop not implemented) vs strategy 404 
          const isEndpointNotFound = responseText.includes('The requested URL was not found') || 
                                    responseText.includes('Not Found') ||
                                    responseText.includes('404');
          
          if (isEndpointNotFound) {
            console.warn('DeploymentService: ⚠️ VM stop endpoint not implemented on this VM version');
            return {
              success: false,
              available: false,
              message: 'VM stop endpoint not implemented on this server version. Strategy continues running until you deploy a new one or VM restarts.',
              deployment_id: deploymentId,
              status: 'endpoint_not_available',
              suggestion: 'The delete button will remove the strategy from UI, but it will continue running on VM in background until replaced.'
            };
          } else {
            // Strategy not found - might already be stopped
            console.warn('DeploymentService: ⚠️ Strategy not found on VM (might already be stopped):', deploymentId);
            return {
              success: true,
              message: 'Strategy not found on VM (might already be stopped)',
              deployment_id: deploymentId,
              status: 'not_found_assumed_stopped'
            };
          }
        }
        
        console.error('DeploymentService: ❌ VM stop failed:', response.status, responseData);
        throw new Error(`VM stop failed (${response.status}): ${responseData.error || responseData.message || 'Unknown error'}`);
      }

      if (!responseData.success) {
        console.error('DeploymentService: ❌ VM rejected stop request:', responseData);
        throw new Error(`VM rejected stop request: ${responseData.error || 'Unknown error'}`);
      }

      console.log('DeploymentService: ✅ VM STRATEGY STOPPED SUCCESSFULLY:', responseData);
      
      return {
        success: true,
        deployment_id: deploymentId,
        message: responseData.data?.message || 'Strategy stopped successfully on VM',
        stopped_at: responseData.data?.stopped_at,
        vm_response: responseData.data
      };
      
    } catch (error) {
      console.error('DeploymentService: 💥 STOP FAILED:', error);
      throw new Error(`Failed to stop strategy on VM: ${error.message}`);
    }
  }

  /**
   * Pause a deployed strategy
   */
  async pauseStrategy(deploymentId: string): Promise<any> {
    // Note: VM doesn't have individual strategy control yet
    // This would require additional endpoint: PUT /api/strategies/{id}/pause
    return {
      available: false,
      message: 'Strategy pause endpoint not yet implemented on VM',
      deployment_id: deploymentId,
      suggestion: 'Strategy control features can be added to VM if needed'
    };
  }

  /**
   * Resume a deployed strategy
   */
  async resumeStrategy(deploymentId: string): Promise<any> {
    // Note: VM doesn't have individual strategy control yet
    // This would require additional endpoint: PUT /api/strategies/{id}/resume
    return {
      available: false,
      message: 'Strategy resume endpoint not yet implemented on VM',
      deployment_id: deploymentId,
      suggestion: 'Strategy control features can be added to VM if needed'
    };
  }

  /**
   * Completely delete a deployed strategy - REAL VM INTEGRATION
   * This removes the strategy from persistent storage permanently
   */
  async deleteStrategy(deploymentId: string): Promise<any> {
    console.log('DeploymentService: 🗑️ DELETING STRATEGY FROM VM:', deploymentId);
    
    try {
      // REAL DELETE: Call VM's /api/strategies/delete/{id} endpoint
      const response = await fetch(`${this.baseUrl}/api/strategies/delete/${deploymentId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...this.getAuthHeaders(),
        },
      });

      const responseText = await response.text();
      let responseData;
      
      try {
        responseData = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error(`VM returned invalid JSON: ${responseText}`);
      }

      if (!response.ok) {
        if (response.status === 404) {
          // Strategy not found - might already be deleted
          console.warn('DeploymentService: ⚠️ Strategy not found on VM (might already be deleted):', deploymentId);
          return {
            success: true,
            message: 'Strategy not found on VM (might already be deleted)',
            deployment_id: deploymentId,
            status: 'not_found_assumed_deleted'
          };
        }
        
        console.error('DeploymentService: ❌ VM delete failed:', response.status, responseData);
        throw new Error(`VM delete failed (${response.status}): ${responseData.error || responseData.message || 'Unknown error'}`);
      }

      if (!responseData.success) {
        console.error('DeploymentService: ❌ VM rejected delete request:', responseData);
        throw new Error(`VM rejected delete request: ${responseData.error || 'Unknown error'}`);
      }

      console.log('DeploymentService: ✅ VM STRATEGY DELETED SUCCESSFULLY:', responseData);
      
      return {
        success: true,
        deployment_id: deploymentId,
        message: responseData.message || 'Strategy deleted successfully from VM',
        deleted_at: responseData.deleted_strategy?.deleted_at,
        strategy_name: responseData.deleted_strategy?.name,
        vm_response: responseData
      };
      
    } catch (error) {
      console.error('DeploymentService: 💥 DELETE FAILED:', error);
      throw new Error(`Failed to delete strategy from VM: ${error.message}`);
    }
  }

  async placeOrder(order: any, tradingMode: string = 'paper'): Promise<any> {
    try {
      const orderWithTradingMode = {
        ...order,
        trading_mode: tradingMode
      };

      const response = await fetch(`${this.baseUrl}/api/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderWithTradingMode)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Order placement failed: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Order placement error:', error);
      throw error;
    }
  }
}

// Export a singleton instance
export const deploymentService = new DeploymentService();
export default deploymentService; 